'use client';

import { styled } from 'styled-components';
import { spaceGrotesk, spaceMono } from '@lib/fonts';
import { trackLink } from '@lib/tracking';
import type { NavPromo } from '@content/navigation/simplified/nav-data';

const PromoLink = styled.a`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.black};
  border-radius: ${({ theme }) => theme.radius.mini};
  color: ${({ theme }) => theme.colors.black};
  display: flex;
  gap: ${({ theme }) => theme.spacing[0.75]};
  padding-block: ${({ theme }) => theme.spacing[0.5]};
  padding-inline: ${({ theme }) => theme.spacing[0.75]};
  transition: background-color var(--nav-row-duration) var(--nav-ease);

  &:hover,
  &:focus-visible {
    background-color: hsla(79, 100%, 67%, 1);
  }
`;

const Copy = styled.span`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.1875rem;
  min-width: 0;
`;

const Head = styled.span`
  align-items: center;
  display: flex;
  gap: 0.375rem;
`;

const Mark = styled.svg`
  display: block;
  flex: 0 0 auto;
  width: 0.9375rem;
`;

const Title = styled.span`
  font-family: var(--font-space-grotesk);
  font-size: ${({ theme }) => theme.typography.fontSize.bodyMd};
  font-weight: ${({ theme }) => theme.typography.fontWeight.body};
  line-height: 1.2;
`;

const Description = styled.span`
  color: ${({ theme }) => theme.colors.grey800};
  font-family: var(--font-space-mono);
  font-size: 0.6875rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.body};
  line-height: 1.35;
`;

type MochinePromoProps = {
  className?: string;
  promo: NavPromo;
  onClick: () => void;
};

const MochinePromo = ({ className, promo, onClick }: MochinePromoProps) => (
  <PromoLink
    className={[spaceGrotesk.variable, spaceMono.variable, className]
      .filter(Boolean)
      .join(' ')}
    href={promo.href}
    onClick={() => {
      trackLink(promo.trackingLabel, promo.trackingProperty);
      onClick();
    }}
    rel="noopener"
    target="_blank"
  >
    <Copy>
      <Head>
        <Mark aria-hidden="true" fill="currentColor" viewBox="0 0 19.94 18.51">
          <polygon points="14.24 0 9.97 4.27 5.7 0 5.7 8.54 9.97 12.82 14.24 8.54 14.24 0" />
          <polygon points="0 2.85 0 18.51 5.7 18.51 5.7 8.54 0 2.85" />
          <polygon points="19.94 2.85 14.24 8.54 14.24 18.51 19.94 18.51 19.94 2.85" />
        </Mark>
        <Title>{promo.title}</Title>
      </Head>
      <Description>{promo.description}</Description>
    </Copy>
  </PromoLink>
);

export default MochinePromo;
